import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const CONTACT_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ContactPage",
      "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/contact#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/contact`,
      "name": "Contact DTPU – Request a Free Electrical Quote NYC",
      "description": "Contact Done to Power Up for a free electrical quote in NYC and Westchester County. Call (646) 750-1474 or fill out our form. 24/7 emergency electrical service available.",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL || "https://example.com" },
          { "@type": "ListItem", "position": 2, "name": "Contact", "item": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/contact` }
        ]
      }
    },
    {
      "@type": "ElectricalContractor",
      "name": "Done to Power Up (DTPU)",
      "telephone": "+16467501474",
      "email": "office@dtpuservices.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "2568 17th Street, Suite 101",
        "addressLocality": "Brooklyn",
        "addressRegion": "NY",
        "postalCode": "11235",
        "addressCountry": "US"
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
          "opens": "07:00",
          "closes": "19:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Sunday",
          "opens": "00:00",
          "closes": "23:59"
        }
      ]
    }
  ]
};

const ContactPage = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-contact';
    script.textContent = JSON.stringify(CONTACT_SCHEMA);
    document.head.appendChild(script);
    document.title = 'Contact DTPU – Request a Free Electrical Quote NYC';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Contact Done to Power Up for a free electrical quote in NYC and Westchester County. Call (646) 750-1474 or fill out our form. 24/7 emergency electrical service available.');
    return () => { document.getElementById('schema-contact')?.remove(); };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [validationError, setValidationError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidationError('');
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      setValidationError('Please fill in all required fields.');
      return;
    }

    if (formData.message.length > 500) {
      setValidationError('Message must be 500 characters or less.');
      return;
    }

    if (!agreedToPrivacy) {
      setValidationError('You must agree to the Privacy Policy before submitting.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d672rj38scpqk7lbjlu0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
        setAgreedToPrivacy(false);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-secondary via-secondary to-gray-800">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
        </div>
        
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-12 text-center">
          <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase mb-4">
            GET IN TOUCH
          </p>
          <h1 className="text-6xl font-bold text-white mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Ready to power up your project? Contact Done to Power Up today for a free consultation and quote. Our team is standing by to help with all your electrical needs.
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-2xl">
            {/* Left Panel - Contact Info */}
            <div className="bg-primary p-12 lg:p-16 relative overflow-hidden">
              {/* Decorative Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImNpcmN1aXQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMiIgZmlsbD0id2hpdGUiLz48bGluZSB4MT0iMjAiIHkxPSIyMCIgeDI9IjQwIiB5Mj0iMjAiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjxsaW5lIHgxPSIyMCIgeTE9IjIwIiB4Mj0iMjAiIHkyPSI0MCIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2NpcmN1aXQpIi8+PC9zdmc+')]"></div>
              </div>

              <div className="relative z-10">
                <h2 className="text-4xl font-bold text-white mb-6">
                  Get In Touch
                </h2>
                <p className="text-white/90 text-base mb-12 leading-relaxed">
                  Have questions or need immediate assistance? Reach out to our team. We're here to help with all your electrical needs.
                </p>

                {/* Contact Information */}
                <div className="space-y-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-base mb-1">Phone</h3>
                      <a href="tel:+16467501474" className="text-white/90 hover:text-white transition-colors duration-300 cursor-pointer">
                        (646) 750-1474
                      </a>
                      <p className="text-white/70 text-sm mt-1">24/7 Emergency Line</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-base mb-1">Email</h3>
                      <a href="mailto:office@dtpuservices.com" className="text-white/90 hover:text-white transition-colors duration-300 cursor-pointer">
                        office@dtpuservices.com
                      </a>
                      <p className="text-white/70 text-sm mt-1">We respond within 1 hour</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-base mb-1">Address</h3>
                      <p className="text-white/90">
                        2568 17th Street, Suit 101<br />
                        Brooklyn, NY 11235
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-base mb-1">Business Hours</h3>
                      <p className="text-white/90">
                        Monday – Saturday: 7:00 AM – 7:00 PM<br />
                        Sunday: 24-Hour Emergency Only
                      </p>
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                <div className="mt-12 pt-8 border-t border-white/20">
                  <h3 className="text-white font-semibold text-base mb-4">Follow Us</h3>
                  <div className="flex items-center space-x-4">
                    <a
                      href="https://linkedin.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-white/30 text-white hover:bg-white hover:text-primary transition-all duration-300 cursor-pointer"
                      aria-label="LinkedIn"
                    >
                      <i className="ri-linkedin-fill text-lg"></i>
                    </a>
                    <a
                      href="https://facebook.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-white/30 text-white hover:bg-white hover:text-primary transition-all duration-300 cursor-pointer"
                      aria-label="Facebook"
                    >
                      <i className="ri-facebook-fill text-lg"></i>
                    </a>
                    <a
                      href="https://instagram.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-white/30 text-white hover:bg-white hover:text-primary transition-all duration-300 cursor-pointer"
                      aria-label="Instagram"
                    >
                      <i className="ri-instagram-fill text-lg"></i>
                    </a>
                    <a
                      href="https://youtube.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-full border-2 border-white/30 text-white hover:bg-white hover:text-primary transition-all duration-300 cursor-pointer"
                      aria-label="YouTube"
                    >
                      <i className="ri-youtube-fill text-lg"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - Contact Form */}
            <div className="bg-white p-12 lg:p-16">
              <h2 className="text-3xl font-bold text-secondary mb-3">
                Request a Quote
              </h2>
              <p className="text-gray-600 text-sm mb-8">
                Fill out the form below and we'll get back to you within 1 hour during business hours.
              </p>

              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-secondary mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-100 border-0 rounded-lg px-4 py-3 text-sm text-secondary focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="John Smith"
                  />
                </div>

                {/* Email and Phone */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-secondary mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-gray-100 border-0 rounded-lg px-4 py-3 text-sm text-secondary focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-secondary mb-2">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-gray-100 border-0 rounded-lg px-4 py-3 text-sm text-secondary focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                {/* Service Type */}
                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-secondary mb-2">
                    Service Type
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full bg-gray-100 border-0 rounded-lg px-4 py-3 text-sm text-secondary focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
                  >
                    <option value="">Select a service</option>
                    <option value="Electrical Repairs">Electrical Repairs</option>
                    <option value="Panel Upgrades">Panel Upgrades</option>
                    <option value="Lighting Installation">Lighting Installation</option>
                    <option value="Smart Home Wiring">Smart Home Wiring</option>
                    <option value="EV Charger Installation">EV Charger Installation</option>
                    <option value="Surge Protection">Surge Protection</option>
                    <option value="Emergency Service">Emergency Service</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-secondary mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full bg-gray-100 border-0 rounded-lg px-4 py-3 text-sm text-secondary focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    placeholder="Tell us about your project or electrical needs..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                {/* Privacy Policy Checkbox */}
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="privacy-policy"
                    name="privacy-policy"
                    checked={agreedToPrivacy}
                    onChange={(e) => { setAgreedToPrivacy(e.target.checked); setValidationError(''); }}
                    className="mt-1 w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer flex-shrink-0"
                    required
                  />
                  <label htmlFor="privacy-policy" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                    I agree to the{' '}
                    <Link
                      to="/privacy-policy"
                      target="_blank"
                      className="text-primary font-medium hover:underline cursor-pointer"
                    >
                      Privacy Policy
                    </Link>{' '}
                    and consent to Done to Power Up collecting and using my information as described.
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                </div>

                {/* Validation Error */}
                {validationError && (
                  <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    {validationError}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-white py-4 rounded-lg font-semibold text-base hover:bg-primary/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-check-line mr-2"></i>
                    Thank you! We've received your message and will respond within 1 hour.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    Something went wrong. Please try again or call us directly.
                  </div>
                )}

                <p className="text-xs text-gray-500 text-center">
                  We respond within 1 hour during business hours
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Hours of Service Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase mb-4">AVAILABILITY</p>
            <h2 className="text-4xl font-bold text-secondary mb-4">Hours of Service</h2>
            <p className="text-gray-600 text-base max-w-xl mx-auto">
              We're here when you need us — from routine work to around-the-clock emergencies.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Monday - Saturday */}
            <div className="bg-white rounded-xl p-8 border-l-4 border-primary text-center hover:shadow-md transition-shadow duration-300">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-primary/10 rounded-full">
                <i className="ri-calendar-check-line text-primary text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-secondary mb-2">Monday – Saturday</h3>
              <p className="text-3xl font-extrabold text-primary mb-1">7AM – 7PM</p>
              <p className="text-sm text-gray-500">Regular Service Hours</p>
            </div>

            {/* Sunday */}
            <div className="bg-white rounded-xl p-8 border-l-4 border-red-500 text-center hover:shadow-md transition-shadow duration-300">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-red-50 rounded-full">
                <i className="ri-alarm-warning-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-secondary mb-2">Sunday</h3>
              <p className="text-3xl font-extrabold text-red-500 mb-1">24 Hours</p>
              <p className="text-sm text-gray-500">Emergency Only</p>
            </div>

            {/* Emergency CTA */}
            <div className="bg-secondary rounded-xl p-8 text-center hover:shadow-md transition-shadow duration-300 flex flex-col justify-center">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-white/10 rounded-full">
                <i className="ri-phone-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Emergency Line</h3>
              <a
                href="tel:+19145016880"
                className="text-2xl font-extrabold text-primary hover:text-primary/80 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                (914) 501-6880
              </a>
              <p className="text-sm text-white/70 mt-1">Call anytime for emergencies</p>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-0 bg-white">
        <div className="w-full h-[400px] shadow-inner">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.15830869428!2d-74.119763973046!3d40.69766374874431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Done to Power Up Location Map"
          ></iframe>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;